import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { UserRoles } from './interface/users.interface';
import { DataService } from './services/data.service';
import { HomeComponentsService } from './services/home-components.service';
import { SupportWhatsappService, WhatsappBubbleState } from './services/support-whatsapp.service';
import { removeHostPreLoginSplash } from './shared/constants/host-pre-login.config';
import { WhiteLabelService } from './services/white-label.service';

declare global {
  interface Window {
    __munilyRemoveHostPreLoginSplash?: () => void;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  title = 'munily-dashboard';
  message: any;
  loading: boolean = false;
  showModal: boolean = false;
  lang = new Subject<string>();
  lang$: Observable<any>;
  showModal$: Observable<any>;
  whatsappBubble$: Observable<WhatsappBubbleState>;

  constructor(
    public dataService: DataService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private homeService: HomeComponentsService,
    private whiteLabelService: WhiteLabelService,
    private supportWhatsapp: SupportWhatsappService,
  ) {
    this.lang$ = this.dataService.getLoading();
    this.whatsappBubble$ = this.supportWhatsapp.whatsappState$();
    this.syncWhiteLabelFromBuilding();
  }

  ngOnInit() {
    // Dejar que el router monte el login antes de quitar el splash MTS.
    setTimeout(() => removeHostPreLoginSplash(), 150);

    this.lang.pipe(startWith(this.translate.getBrowserLang())).subscribe((lang) => {
      const appLang = this.dataService.getAppLanguage();
      const selectedForStore = appLang ? appLang : lang;
      const resolvedLang = this.dataService.setLanguage(selectedForStore);
      const langCode = (
        resolvedLang ||
        selectedForStore ||
        'es'
      )
        .toString()
        .trim()
        .split('-')[0];
      // Sin esto TranslateService no carga los JSON antes del shell (burbuja WhatsApp); get/instant devuelven la clave.
      this.translate.use(langCode);
    });

    this.lang$.subscribe((state) => {
      setTimeout(() => {
        if (state && typeof state.isLoading !== 'undefined') {
          this.loading = state.isLoading;
        }
      }, 200);
    });

    let user = this.dataService.getUser();
    const authorizedRole = user?.roles?.some((rol: string) => [UserRoles.admin, UserRoles.customized].includes(UserRoles[rol]));
    if (user && user.roles && authorizedRole) this.refreshLocalStorageData();

    this.supportWhatsapp.init();
  }

  /** Aplica título/favicon y ruta de login según whiteLabel del edificio. */
  private syncWhiteLabelFromBuilding(): void {
    const building = this.dataService.getBuilding();
    if (!building) return;
    this.whiteLabelService.setSessionLoginPathFromBuilding(building);
    this.whiteLabelService.applyFromBuilding(building);
  }

  refreshLocalStorageData() {
    this.homeService.getRefreshBuildingConfiguration().subscribe(
      (data: any) => {
        let currentUser = JSON.parse(localStorage.getItem('currentUser'));
        const response: any = data.data;
        if (typeof response.building == 'string') {
          this.toastr.error(this.translate.instant('toastrMessage.error.refreshData'));
          return;
        }
        const previousBuilding = currentUser?.buildings?.[0];
        const preservedUnitId = previousBuilding?.unitId ?? previousBuilding?.unit;
        const preservedUnit = previousBuilding?.unit;
        // Preservar whiteLabel con projects (viene del login con merge de company)
        const preservedWhiteLabel = previousBuilding?.config?.whiteLabel;
        let building = response.building;
        building.roles = response.roles;
        building.userStatus = response.status;
        building.id = building._id ?? building.id;
        if (preservedUnitId != null) building.unitId = preservedUnitId;
        if (preservedUnit != null) building.unit = preservedUnit;
        // Preservar whiteLabel si tenía projects (el refresh no hace merge con company)
        if (preservedWhiteLabel?.projects) {
          building.config = building.config || {};
          building.config.whiteLabel = preservedWhiteLabel;
        }
        currentUser.buildings[0] = building;
        this.dataService.setUser(currentUser);
      },
      (error) => {
        console.log(error);
        this.toastr.error(this.translate.instant('toastrMessage.error.refreshData'));
      },
    );
  }
}
